import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { api } from '../lib/api';
import type { Filters } from '../lib/filters';
import { flyOptions } from '../lib/motion';
import { insideMexico } from '../lib/mexico';
import { overlayPadding } from '../lib/mapPadding';

interface Props {
  filters: Filters;
}

/**
 * Componente invisible que vive dentro del <MapContainer>. Cada vez
 * que cambia un filtro "global" (cveEnt, q, channel, status, type),
 * pide los results SIN bbox, calcula su extent y hace fitBounds.
 *
 * Sin esto, si filtras por Yucatán mientras el mapa está en CDMX,
 * no ves ningún marker porque PropertyMarkers usa el bbox actual.
 *
 * Diseño:
 * - Skipea si los filtros están vacíos (sin razón para mover el mapa).
 * - Skipea si todos los results coinciden geográficamente con el viewport
 *   actual (>10% overlap) — evita movimientos innecesarios.
 * - Solo hace fitBounds si hay >=2 features con coords (1 sola → flyTo
 *   con zoom 14; ninguna → no-op).
 */
export function FlyToOnFilters({ filters }: Props) {
  const map = useMap();
  const lastFiltersRef = useRef<string>(JSON.stringify(filters));

  useEffect(() => {
    const currentKey = JSON.stringify(filters);
    if (currentKey === lastFiltersRef.current) return;
    lastFiltersRef.current = currentKey;

    // Sin filtros — no movemos el mapa (estado inicial / reset).
    const hasGlobalFilter =
      !!filters.cveEnt || !!filters.q || !!filters.legalChannel ||
      !!filters.status || !!filters.propertyType;
    if (!hasGlobalFilter) return;

    const ctrl = new AbortController();
    api
      .propertiesGeoJson({ limit: 500, filters }, ctrl.signal)
      .then((fc) => {
        const points: [number, number][] = [];
        for (const f of fc.features) {
          if (f.geometry) {
            const [lng, lat] = f.geometry.coordinates;
            // Fuera de México = coordenada mala de la fuente: estiraría la
            // caja hasta el océano.
            if (insideMexico(lng, lat)) {
              points.push([lat, lng]);
            }
          }
        }
        if (points.length === 0) return;
        if (points.length === 1) {
          const p = points[0];
          if (p) map.flyTo(p, 14, flyOptions(0.6));
          return;
        }
        // Bounds de leaflet: [[swLat, swLng], [neLat, neLng]].
        let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
        for (const [lat, lng] of points) {
          if (lat < minLat) minLat = lat;
          if (lat > maxLat) maxLat = lat;
          if (lng < minLng) minLng = lng;
          if (lng > maxLng) maxLng = lng;
        }
        map.flyToBounds([[minLat, minLng], [maxLat, maxLng]], {
          ...overlayPadding(map),
          maxZoom: 14,
          ...flyOptions(0.6),
        });
      })
      .catch(() => {});

    return () => ctrl.abort();
  }, [filters, map]);

  return null;
}
